// Печать этикеток со штрихкодами. Открывает окно печати (как документы/реестры),
// раскладку выбирает по формату: лист A4 (сетка) или рулонный термопринтер.
import { openDocWindow } from './deals-api';
import { barcodeSvgMarkup } from './barcode-svg';

export type LabelData = {
  code: string; // штрихкод (Code128)
  title: string; // название узла/детали
  sub?: string; // № сделки · изделие
  qtyNote?: string; // напр. «2 из 3»
};

export type LabelFormat = 'a4' | 'roll';

const esc = (v: unknown) =>
  String(v ?? '').replace(
    /[&<>]/g,
    (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[m] as string,
  );

function labelHtml(l: LabelData, bcHeight: string): string {
  return (
    `<div class="label">` +
    `<div class="bc">${barcodeSvgMarkup(l.code, bcHeight)}</div>` +
    `<div class="code">${esc(l.code)}</div>` +
    `<div class="t">${esc(l.title)}${l.qtyNote ? ` <span class="qty">· ${esc(l.qtyNote)}</span>` : ''}</div>` +
    (l.sub ? `<div class="sub">${esc(l.sub)}</div>` : '') +
    `</div>`
  );
}

// Раскладки: A4 — сетка самоклейки (3 колонки, реж. по границам); рулон — одна этикетка на страницу.
const LAYOUT: Record<LabelFormat, { page: string; sheet: string; label: string; bc: string }> = {
  a4: {
    page: '@page { size: A4; margin: 8mm; }',
    sheet: '.sheet { display: flex; flex-wrap: wrap; gap: 2mm; }',
    label:
      '.label { width: 62mm; height: 36mm; padding: 2mm; border: 1px dashed #bbb; break-inside: avoid; }',
    bc: '12mm',
  },
  roll: {
    page: '@page { size: 58mm 40mm; margin: 0; }',
    sheet: '.sheet { display: block; }',
    label: '.label { width: 58mm; height: 40mm; padding: 3mm; border: none; break-after: page; }',
    bc: '15mm',
  },
};

export function printLabels(opts: {
  title: string;
  format: LabelFormat;
  labels: LabelData[];
}): void {
  const { title, format, labels } = opts;
  if (labels.length === 0) return;
  const L = LAYOUT[format];
  const body = labels.map((l) => labelHtml(l, L.bc)).join('');
  const html = `<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #000; }
  ${L.page}
  ${L.sheet}
  .label { display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; }
  .bc { line-height: 0; }
  .code { font-family: 'Courier New', monospace; font-size: 8pt; letter-spacing: .5px; margin-top: 1mm; }
  .t { font-weight: 700; font-size: 9pt; line-height: 1.15; overflow: hidden; }
  .t .qty { color: #000; }
  .sub { font-size: 7pt; color: #555; }
  .toolbar { padding: 8px 12px; background: #f4f4f4; border-bottom: 1px solid #ccc; font: 13px 'Segoe UI', Arial; }
  .toolbar button { font: inherit; padding: 4px 12px; cursor: pointer; }
  @media print { .toolbar { display: none; } }
</style></head>
<body>
  <div class="toolbar">
    Этикеток: <b>${labels.length}</b> · формат: <b>${format === 'a4' ? 'лист A4' : 'рулон 58×40 мм'}</b>
    &nbsp;<button onclick="window.print()">🖨 Печать</button>
  </div>
  <div class="sheet">${body}</div>
  <script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 150); });</script>
</body></html>`;
  openDocWindow(html);
}
